import React, { ReactNode, useEffect } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDrag } from 'react-dnd';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import Button from '@client/components/Button';
import undo from '@client/assets/images/undo.png';
import styles from './styles/DraggableCard.module.css';
import ItemCard from './ItemCard';

export const ItemType = 'PDFItem';

export type PdfDragItem = {
	type: typeof ItemType;
	itemId: number | string;
	content: ReactNode;
};

type DraggableCardProps = {
	id: number | string;
	added: boolean;
	children?: ReactNode;
	enabled?: boolean;
	onRemoveClick: () => void;
	onAddItem: () => void;
	isDisabled?: boolean;
};

const DraggableCard = ({
	id,
	added,
	children,
	enabled = true,
	onRemoveClick,
	onAddItem,
	isDisabled,
}: DraggableCardProps) => {
	const [{ dragging }, dragRef, preview] = useDrag<PdfDragItem, void, { dragging: boolean }>({
		item: {
			type: ItemType,
			itemId: id,
			content: children,
		},
		canDrag: () => (!added && enabled && !isDisabled),
		collect: (monitor) => ({
			dragging: monitor.isDragging(),
		}),
	});

	useEffect(() => {
		preview(getEmptyImage(), { captureDraggingState: true });
	}, [preview]);

	return (
		<div
			ref={dragRef}
			role="button"
			tabIndex={0}
			onClick={isDisabled ? undefined : onAddItem}
			onKeyDown={(e) => e.key === 'Enter' && onAddItem()}
			className={classNames(styles.container, {
				[styles.dragging]: dragging,
				[styles.added]: added,
			})}
		>
			<div className={styles.dragBackground} />
			{added && (
				<Button
					type="primary"
					className={styles.removeButton}
					onClick={(e) => {
						e?.stopPropagation();
						onRemoveClick();
					}}
				>
					<img
						alt="Undo"
						className={styles.undo}
						src={undo}
					/>
				</Button>
			)}
			<ItemCard>
				{isDisabled &&
				(
					<div>
						<FontAwesomeIcon
							icon={faLock}
							color="black"
						/>
						<span style={{ marginLeft: 10 }}>
							Different currency
						</span>
					</div>
				)}
				{children}
			</ItemCard>
		</div>
	);
};

export default DraggableCard;
